import { createFileRoute } from '@tanstack/react-router';
import { Alert, Badge, Button, Card, Divider, Group, Stack, Tabs, Text, Title, Loader } from '@mantine/core';
import { IconAlertTriangle, IconCalendarMonth, IconClock, IconLogin, IconLogout, IconRefresh } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { AttendanceCalendar } from '@/features/attendance/AttendanceCalendar';
import { useMyEmployeeId } from '@/features/workforce/useMyEmployee';
import { fetchCurrentStatus, postClock, fetchAttendanceRange, parseBeIso } from '@/features/attendance/api';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { EmptyState } from '@/shared/components/EmptyState';

export const Route = createFileRoute('/_app/attendance')({ component: AttendancePage });

function AttendancePage() {
  return (
    <Stack>
      <Title order={1}>Presenze</Title>
      <Tabs defaultValue="today" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="today" leftSection={<IconClock size={16} />}>Oggi</Tabs.Tab>
          <Tabs.Tab value="calendar" leftSection={<IconCalendarMonth size={16} />}>Calendario mensile</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="today" pt="md"><TodayPanel /></Tabs.Panel>
        <Tabs.Panel value="calendar" pt="md"><AttendanceCalendar /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function formatHm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function TodayPanel() {
  const qc = useQueryClient();
  const myEmployeeId = useMyEmployeeId();
  const [now, setNow] = useState(() => dayjs());

  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 30_000);
    return () => clearInterval(t);
  }, []);

  const statusQ = useQuery({
    queryKey: ['attendance-status'],
    queryFn: () => fetchCurrentStatus(),
    refetchInterval: 60_000,
  });

  const todayIso = dayjs().format('YYYY-MM-DD');
  const todayEventsQ = useQuery({
    queryKey: ['attendance-today', myEmployeeId, todayIso],
    enabled: Boolean(myEmployeeId),
    queryFn: () => fetchAttendanceRange(
      myEmployeeId!,
      dayjs().startOf('day').toISOString(),
      dayjs().endOf('day').toISOString(),
    ),
    refetchInterval: 60_000,
  });

  const isIn = statusQ.data?.status === 'in';
  const startedAt = statusQ.data?.lastTimestamp && isIn ? dayjs(parseBeIso(statusQ.data.lastTimestamp)) : null;
  const sessionMinutes = startedAt ? now.diff(startedAt, 'minute') : 0;

  // Totale minuti lavorati oggi (somma di tutti i pair in/out chiusi + sessione corrente se aperta)
  const todayTotalMinutes = useMemo(() => {
    const events = (todayEventsQ.data ?? []).slice().sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    let total = 0;
    let lastIn: string | null = null;
    for (const ev of events) {
      if (ev.status === 1) lastIn = ev.timestamp;
      else if (ev.status === 0 && lastIn) {
        total += Math.max(0, (parseBeIso(ev.timestamp).getTime() - parseBeIso(lastIn).getTime()) / 60000);
        lastIn = null;
      }
    }
    if (lastIn) {
      total += Math.max(0, now.toDate().getTime() - parseBeIso(lastIn).getTime()) / 60000;
    }
    return Math.round(total);
  }, [todayEventsQ.data, now]);

  const clock = useMutation({
    mutationFn: () => postClock(isIn ? 'out' : 'in'),
    onSuccess: () => {
      notifications.show({ color: 'green', message: isIn ? 'Uscita registrata' : 'Entrata registrata' });
      qc.invalidateQueries({ queryKey: ['attendance-status'] });
      qc.invalidateQueries({ queryKey: ['attendance-today'] });
      qc.invalidateQueries({ queryKey: ['attendance-month'] });
    },
    onError: (err: unknown) => {
      let title = 'Timbratura non riuscita';
      let message = 'Riprova fra qualche secondo';
      if (err instanceof JsonApiHttpError) {
        const detail = err.errors[0]?.detail ?? err.errors[0]?.title ?? err.message;
        if (err.status === 409) {
          title = 'Timbratura duplicata';
          message = detail || 'Hai gia timbrato negli ultimi 30 secondi. Attendi prima di riprovare.';
        } else if (err.status === 400) {
          title = 'Dati non validi';
          message = detail || 'Il server ha rifiutato la timbratura.';
        } else if (err.status === 401 || err.status === 403) {
          title = 'Non autorizzato';
          message = 'Sessione scaduta. Effettua di nuovo il login.';
        } else if (detail) {
          message = detail;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }
      notifications.show({ color: 'red', title, message, autoClose: 6000 });
    },
  });

  // Filtra al giorno LOCALE corrente (il fetch range usa start/endOfDay locale + toISOString che e' UTC,
  // quindi puo' restituire timbrature di ieri o domani in altri TZ; rifiltriamo qui per sicurezza).
  const todayLocal = dayjs().format('YYYY-MM-DD');
  const eventsToday = (todayEventsQ.data ?? [])
    .filter((e) => dayjs(parseBeIso(e.timestamp)).format('YYYY-MM-DD') === todayLocal)
    .slice()
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  const lastEvent = eventsToday[eventsToday.length - 1];
  const hasOpenSession = lastEvent?.status === 1;

  return (
    <Stack>
      <Card withBorder padding="xl" radius="md">
        <Stack align="center" gap="lg">
          <IconClock size={64} opacity={0.4} />
          <div style={{ textAlign: 'center' }}>
            <Title order={2}>{isIn ? 'In servizio' : 'Fuori servizio'}</Title>
            {startedAt && <Text c="dimmed">Entrato alle {startedAt.format('HH:mm')}</Text>}
            {isIn && (
              <Badge mt="xs" size="xl" color="green">
                Sessione: {formatHm(sessionMinutes)}
              </Badge>
            )}
            {todayTotalMinutes > 0 && (
              <Text mt="xs" size="sm" c="dimmed">
                Totale oggi: <b>{formatHm(todayTotalMinutes)}</b>
              </Text>
            )}
          </div>
          <Group>
            <Button
              size="xl"
              leftSection={isIn ? <IconLogout size={20} /> : <IconLogin size={20} />}
              color={isIn ? 'red' : 'green'}
              loading={clock.isPending || statusQ.isLoading}
              onClick={() => clock.mutate()}
            >
              {isIn ? 'Esci' : 'Entra'}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Timbrature di oggi</Title>
          <Button size="xs" variant="subtle" leftSection={<IconRefresh size={14} />} onClick={() => { qc.invalidateQueries({ queryKey: ['attendance-status'] }); qc.invalidateQueries({ queryKey: ['attendance-today'] }); }}>
            Aggiorna
          </Button>
        </Group>
        {todayEventsQ.isLoading ? <Loader size="sm" /> : eventsToday.length === 0 ? (
          <EmptyState
            icon={IconClock}
            title="Nessuna timbratura oggi"
            description="Quando timbri, le entrate e uscite compariranno qui."
            py="md"
          />
        ) : (
          <Stack gap={4}>
            {eventsToday.map((ev, i) => (
              <Group key={`${ev.timestamp}-${i}`} gap="xs">
                <Badge color={ev.status === 1 ? 'green' : 'red'} variant="light" size="sm" leftSection={ev.status === 1 ? <IconLogin size={12} /> : <IconLogout size={12} />}>
                  {ev.status === 1 ? 'IN' : 'OUT'}
                </Badge>
                <Text size="sm">{dayjs(parseBeIso(ev.timestamp)).format('HH:mm:ss')}</Text>
                {ev.locationType != null && <Text size="xs" c="dimmed">· {ev.locationType === 0 ? 'sede' : ev.locationType === 1 ? 'remoto' : `loc ${ev.locationType}`}</Text>}
              </Group>
            ))}
            {hasOpenSession && (
              <>
                <Divider my={4} />
                <Alert color="blue" icon={<IconAlertTriangle size={16} />} variant="light">
                  Sessione aperta dalle {dayjs(parseBeIso(lastEvent!.timestamp)).format('HH:mm')} — ricorda di timbrare l'uscita a fine giornata.
                </Alert>
              </>
            )}
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
